import React from 'react';
import styled from 'styled-components';
import { COMPANY_TABS, COMPANY_TAB_VALUES } from 'constants/tabs';
import FeatureFlag from 'data/FeatureFlag';
import Vendor from 'data/Vendor';
import Company from 'data/Company';
import Customer from 'data/Customer';
import MDMGeneralTab from 'common/Companies/MDMGeneralTab';
import OrderHistoryTab from 'common/Companies/OrderHistoryTab';
import CompanyDocumentsList from 'common/Companies/CompanyDocumentsList';
import PricebooksTab from 'common/Companies/PricebooksTab';
import SalesOrder from 'common/Companies/SalesOrder';
import VendorRelationsTab from 'common/Companies/VendorRelationsTab';
import BusinessUnitRelationsTab from 'common/Companies/BusinessUnitRelationsTab';
import { checkUserSalesOrderVendorOption, isSalesOrderEnabled } from 'utils/salesorder';
import { enablePricebooks } from 'utils/pricebooks';
import { CreditTrackingMode } from 'types/credit.types';
import { BusinessUnit } from 'data/BusinessUnit';
import { BUSINESS_UNIT_RELATIONS } from 'constants/credit';

const EmptyMessage = styled.span`
  font-size: 24px;
  padding: 24px;
`;

interface TabsConfig {
  isModernUI: boolean;
  tabs: any[];
  tabContent: React.ReactNode[];
}

export interface TabsProps {
  featureFlags: FeatureFlag;
  user: any;
  isVendorAcc: boolean;
  selectedVendor?: Vendor;
  companyDetails: Company;
  companyCustomers: Customer[];
  switchTab: (index: number) => void;
  mdmId: string;
  creditId: string;
  isConnected: boolean;
  creditInfo: any;
  vendorCompanyDetails: any;
  creditTrackingMode: CreditTrackingMode;
  mdmApplyReq: any;
  updateCreditReq: any;
  createCreditReq: any;
  isInvalid: boolean;
  setIsInvalid: (invalid: boolean) => void;
  updateVendor?: (name: string) => void;
  updateBusinessUnit?: (id: number) => void;
  companyId: number;
  error?: string;
  selectedBusinessUnit?: BusinessUnit;
  vendorsList?: Vendor[];
  initialVendor?: Vendor;
  initialBusinessUnit?: BusinessUnit;
  hasActiveSubscriptions?: boolean;
}

const useCompanyTabs = ({
  featureFlags,
  user,
  isVendorAcc,
  selectedVendor,
  companyDetails,
  companyCustomers,
  switchTab,
  mdmId,
  creditId,
  isConnected,
  creditInfo,
  vendorCompanyDetails,
  creditTrackingMode,
  mdmApplyReq,
  updateCreditReq,
  createCreditReq,
  isInvalid,
  setIsInvalid,
  updateVendor,
  updateBusinessUnit,
  companyId,
  error,
  selectedBusinessUnit,
  vendorsList,
  initialVendor,
  initialBusinessUnit,
  hasActiveSubscriptions
}: TabsProps): TabsConfig => {
  // Determine if modern UI should be used
  const isModernUI = featureFlags.pricebooks && featureFlags.pricebooks.enabled;

  // Get vendor tabs
  const getVendorTabs = () => {
    return COMPANY_TABS.filter(tab => {
      if (tab.value === 'Sales Order') {
        return checkUserSalesOrderVendorOption(user, selectedVendor, tab.value) &&
          isSalesOrderEnabled(featureFlags, 'Sales Order');
      }
      return enablePricebooks(featureFlags, tab.value);
    });
  };

  // Get admin tabs
  const getAdminTabs = () => {
    const tabs = COMPANY_TABS.filter(tab => {
        if (tab.value === 'Sales Order') {
          return checkUserSalesOrderVendorOption(user, selectedVendor, tab.value) &&
            isSalesOrderEnabled(featureFlags, 'Sales Order');
        }
        return enablePricebooks(featureFlags, tab.value);
      });

    // Rename the first tab for admin view to Business unit relations
    return tabs.map((tab, index) => {
      if (index === 0) {
        return {
          ...tab,
          value: COMPANY_TAB_VALUES.BUSINESS_UNIT_RELATIONS
        };
      }
      return tab;
    });
  };

  // Get business unit tabs (same structure as vendor tabs - MDMGeneralTab first)
  const getBuTabs = () => {
    return COMPANY_TABS.filter(tab => {
      if (tab.value === 'Sales Order') {
        return checkUserSalesOrderVendorOption(user, selectedVendor, tab.value) &&
          isSalesOrderEnabled(featureFlags, 'Sales Order');
      }
      return enablePricebooks(featureFlags, tab.value);
    });
  };

  // Determine which tabs to show based on user type
  const isVendorView = isVendorAcc || !!selectedVendor;
  const isBuView = !!selectedBusinessUnit && !isVendorView;
  const isAdminView = !isVendorView && !isBuView;
  const tabs = isVendorView ? getVendorTabs() : isBuView ? getBuTabs() : getAdminTabs();

  // Generate the tab content based on user type
  const renderVendorTabContent = () => {
    const generalTabContent = selectedBusinessUnit ? (
      <MDMGeneralTab
        selectedBusinessUnit={selectedBusinessUnit}
        companyId={companyDetails.companyId}
        mdmId={mdmId}
        creditId={creditId}
        isConnected={isConnected}
        creditInfo={creditInfo}
        vendorCompanyDetails={vendorCompanyDetails}
        creditTrackingMode={creditTrackingMode}
        mdmApplyReq={mdmApplyReq}
        updateCreditReq={updateCreditReq}
        createCreditReq={createCreditReq}
        isInvalid={isInvalid}
        setIsInvalid={setIsInvalid}
        error={error}
        vendorsList={vendorsList}
        initialVendor={initialVendor}
        initialBusinessUnit={initialBusinessUnit}
        hasActiveSubscriptions={hasActiveSubscriptions}
        featureFlags={featureFlags}
      />
    ) : (
      <EmptyMessage>{BUSINESS_UNIT_RELATIONS.PLEASE_SELECT_BUSINESS_UNIT}</EmptyMessage>
    );

    return [
      generalTabContent,
      <OrderHistoryTab
        id={companyId}
        selectedVendor={selectedVendor}
        companyId={companyDetails.companyId}
        featureFlags={featureFlags}
        selectedBusinessUnit={selectedBusinessUnit}
      />,
      <CompanyDocumentsList
        selectedVendor={selectedVendor}
        companyId={companyDetails.companyId}
        selectedBusinessUnit={selectedBusinessUnit}
      />,
     <PricebooksTab
          selectedVendor={selectedVendor}
          companyId={companyDetails.companyId}
          companyName={companyDetails.companyName}
          companyCustomers={companyCustomers || []}
          selectedBusinessUnit={selectedBusinessUnit}
          featureFlags={featureFlags}
        />,
      <SalesOrder
        selectedBusinessUnit={selectedBusinessUnit}
        companyId={companyDetails.companyId}
        switchTab={switchTab}
      />
    ];
  };

  const renderBuTabContent = () => {
    return [
      <MDMGeneralTab
        selectedBusinessUnit={selectedBusinessUnit}
        companyId={companyDetails.companyId}
        mdmId={mdmId}
        creditId={creditId}
        isConnected={isConnected}
        creditInfo={creditInfo}
        vendorCompanyDetails={vendorCompanyDetails}
        creditTrackingMode={creditTrackingMode}
        mdmApplyReq={mdmApplyReq}
        updateCreditReq={updateCreditReq}
        createCreditReq={createCreditReq}
        isInvalid={isInvalid}
        setIsInvalid={setIsInvalid}
        error={error}
        vendorsList={vendorsList}
        initialVendor={initialVendor}
        initialBusinessUnit={initialBusinessUnit}
        hasActiveSubscriptions={hasActiveSubscriptions}
        featureFlags={featureFlags}
      />,
      <OrderHistoryTab
        id={companyId}
        selectedVendor={selectedVendor}
        selectedBusinessUnit={selectedBusinessUnit}
        companyId={companyDetails.companyId}
        featureFlags={featureFlags}
      />,
      <CompanyDocumentsList
        selectedVendor={selectedVendor}
        companyId={companyDetails.companyId}
        selectedBusinessUnit={selectedBusinessUnit}
      />,
      <PricebooksTab
        selectedVendor={selectedVendor}
        companyId={companyDetails.companyId}
        companyName={companyDetails.companyName}
        companyCustomers={companyCustomers || []}
        selectedBusinessUnit={selectedBusinessUnit}
        featureFlags={featureFlags}
      />,
      <SalesOrder
        selectedBusinessUnit={selectedBusinessUnit}
        companyId={companyDetails.companyId}
        switchTab={switchTab}
      />
    ];
  };

  const renderAdminTabContent = () => {
    const content = [
      <BusinessUnitRelationsTab
        companyId={companyDetails.companyId}
        updateBusinessUnit={updateBusinessUnit}
      />,
      <OrderHistoryTab
        id={companyId}
        selectedVendor={selectedVendor}
        companyId={companyDetails.companyId}
        featureFlags={featureFlags}
        selectedBusinessUnit={selectedBusinessUnit}
      />,
      <CompanyDocumentsList
        selectedVendor={selectedVendor}
        companyId={companyDetails.companyId}
      />,
      <PricebooksTab
          selectedVendor={selectedVendor}
          companyId={companyDetails.companyId}
          companyName={companyDetails.companyName}
          companyCustomers={companyCustomers || []}
          featureFlags={featureFlags}
        />
    ];

    // Only add Sales Order tab if it's enabled in feature flags
    if (featureFlags.salesOrder && featureFlags.salesOrder.enabled) {
      content.push(
        <SalesOrder
          selectedBusinessUnit={selectedBusinessUnit}
          companyId={companyDetails.companyId}
          switchTab={switchTab}
        />
      );
    }

    return content;
  };

  const tabContent = isVendorView
    ? renderVendorTabContent()
    : isBuView
      ? renderBuTabContent()
      : renderAdminTabContent();

  return {
    isModernUI,
    tabs,
    tabContent
  };
};

export default useCompanyTabs;
